import type { AuthSessionPayload, AuthUser } from '@/contexts/authStateContext';

export type ModuleRw = { read: boolean; write: boolean };

export type AccessCapabilities = {
  projects: {
    read: boolean;
    status: boolean;
    notes: boolean;
    comments: boolean;
    files: boolean;
    write: boolean;
    /** Править глоссарий книги и общий словарь ударений */
    glossary: boolean;
  };
  schedule: { read: boolean; read_all: boolean; write: boolean };
  readers: ModuleRw;
  fonoteka: ModuleRw;
  studios: ModuleRw;
  legal_entities: ModuleRw;
  persons: ModuleRw;
  users: ModuleRw;
  astra: boolean;
  prices: boolean;
  manage_access: boolean;
  hasAnyGrant: boolean;
  canCreateProject: boolean;
};

export type AccessPreview = {
  proposalId: string;
  kind: string;
  explanation: string;
  expiresAt: string;
  target: { type: 'person' | 'group'; id: string; label: string };
};

export const EMPTY_CAPABILITIES: AccessCapabilities = {
  projects: {
    read: false,
    status: false,
    notes: false,
    comments: false,
    files: false,
    write: false,
    glossary: false,
  },
  schedule: { read: false, read_all: false, write: false },
  readers: { read: false, write: false },
  fonoteka: { read: false, write: false },
  studios: { read: false, write: false },
  legal_entities: { read: false, write: false },
  persons: { read: false, write: false },
  users: { read: false, write: false },
  astra: false,
  prices: false,
  manage_access: false,
  hasAnyGrant: false,
  canCreateProject: false,
};

function parseBool(value: unknown): boolean {
  return value === true;
}

function parseModuleRw(value: unknown): ModuleRw | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  return { read: parseBool(row.read), write: parseBool(row.write) };
}

function parseSchedule(value: unknown): AccessCapabilities['schedule'] | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  return {
    read: parseBool(row.read),
    read_all: parseBool(row.read_all),
    write: parseBool(row.write),
  };
}

function parseProjects(value: unknown): AccessCapabilities['projects'] | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  return {
    read: parseBool(row.read),
    status: parseBool(row.status),
    notes: parseBool(row.notes),
    comments: parseBool(row.comments),
    files: parseBool(row.files),
    write: parseBool(row.write),
    glossary: parseBool(row.glossary),
  };
}

export function parseAccessCapabilities(raw: unknown): AccessCapabilities {
  if (!raw || typeof raw !== 'object') return EMPTY_CAPABILITIES;
  const source = raw as Record<string, unknown>;
  const projects = parseProjects(source.projects);
  const schedule = parseSchedule(source.schedule);
  const readers = parseModuleRw(source.readers);
  const fonoteka = parseModuleRw(source.fonoteka) ?? { read: false, write: false };
  const studios = parseModuleRw(source.studios);
  const legal_entities = parseModuleRw(source.legal_entities);
  const persons = parseModuleRw(source.persons);
  const users = parseModuleRw(source.users);
  if (!projects || !schedule || !readers || !studios || !legal_entities || !persons || !users) {
    return EMPTY_CAPABILITIES;
  }
  return {
    projects,
    schedule,
    readers,
    fonoteka,
    studios,
    legal_entities,
    persons,
    users,
    astra: parseBool(source.astra),
    prices: parseBool(source.prices),
    manage_access: parseBool(source.manage_access),
    hasAnyGrant: parseBool(source.hasAnyGrant),
    canCreateProject: parseBool(source.canCreateProject),
  };
}

function parsePreviewTarget(value: unknown): AccessPreview['target'] | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const type = row.type;
  if (type !== 'person' && type !== 'group') return null;
  if (typeof row.id !== 'string' || typeof row.label !== 'string') return null;
  return { type, id: row.id, label: row.label };
}

export function parseAccessPreview(raw: unknown): AccessPreview | null {
  if (raw == null) return null;
  if (typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.proposalId !== 'string') return null;
  if (typeof row.kind !== 'string') return null;
  if (typeof row.explanation !== 'string') return null;
  if (typeof row.expiresAt !== 'string') return null;
  const target = parsePreviewTarget(row.target);
  if (!target) return null;
  return {
    proposalId: row.proposalId,
    kind: row.kind,
    explanation: row.explanation,
    expiresAt: row.expiresAt,
    target,
  };
}

export function parseGroupSlug(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export type AuthSessionResponseBody = {
  success?: boolean;
  user?: AuthUser;
  sessionExpiresAt?: string;
  capabilities?: unknown;
  preview?: unknown;
  group_slug?: unknown;
};

function parseAuthUser(raw: unknown): AuthUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.id !== 'string') return null;
  if (typeof row.first_name !== 'string') return null;
  if (typeof row.last_name !== 'string') return null;
  const user: AuthUser = {
    id: row.id,
    first_name: row.first_name,
    last_name: row.last_name,
  };
  if (row.email === null || typeof row.email === 'string') {
    user.email = row.email;
  }
  if (row.role === null || typeof row.role === 'string') {
    user.role = row.role;
  }
  if (row.access === null || typeof row.access === 'string') {
    user.access = row.access;
  }
  return user;
}

export function parseAuthSessionBody(body: AuthSessionResponseBody): AuthSessionPayload | null {
  if (!body.success || !body.user) return null;
  const user = parseAuthUser(body.user);
  if (!user) return null;
  return {
    user,
    sessionExpiresAt: typeof body.sessionExpiresAt === 'string' ? body.sessionExpiresAt : undefined,
    capabilities: parseAccessCapabilities(body.capabilities),
    preview: parseAccessPreview(body.preview),
    group_slug: parseGroupSlug(body.group_slug),
  };
}

const NAV_MODULES = [
  { path: '/projects', read: (c: AccessCapabilities) => c.projects.read },
  { path: '/schedule', read: (c: AccessCapabilities) => c.schedule.read },
  { path: '/readers', read: (c: AccessCapabilities) => c.readers.read },
  { path: '/fonoteka', read: (c: AccessCapabilities) => c.fonoteka.read },
  { path: '/studios', read: (c: AccessCapabilities) => c.studios.read },
] as const;

export function hasTrackerNavAccess(capabilities: AccessCapabilities): boolean {
  return NAV_MODULES.some((module) => module.read(capabilities));
}

/** Стартовый раздел по правам. projectsPath — куда ведут проекты: таблица старого дизайна или страница нового (lib/designChoice). */
export function resolveDefaultHomePath(capabilities: AccessCapabilities, projectsPath = '/projects'): string {
  if (capabilities.projects.read) return projectsPath;
  for (const module of NAV_MODULES.slice(1)) {
    if (module.read(capabilities)) return module.path;
  }
  return projectsPath;
}

export function isModulePathAllowed(pathname: string, capabilities: AccessCapabilities): boolean {
  if (pathname === '/' || pathname === '/projects' || pathname.startsWith('/projects/')) {
    return capabilities.projects.read;
  }
  if (pathname === '/schedule' || pathname.startsWith('/schedule/')) {
    return capabilities.schedule.read;
  }
  if (pathname === '/readers' || pathname.startsWith('/readers/')) {
    return capabilities.readers.read;
  }
  if (pathname === '/fonoteka' || pathname.startsWith('/fonoteka/')) {
    return capabilities.fonoteka.read;
  }
  if (pathname === '/studios' || pathname.startsWith('/studios/')) {
    return capabilities.studios.read;
  }
  /** Раздел нового дизайна; пока в нём только проекты. */
  if (pathname === '/new' || pathname.startsWith('/new/')) {
    return capabilities.projects.read;
  }
  return true;
}
